import type { SxProps, Theme } from '@mui/material/styles';

export const connectPageStyles: Record<string, SxProps<Theme>> = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    overflow: 'hidden',
  },
  // Fills the space between nav and summary bar; scrolls internally only if
  // the form is taller than the viewport, so the summary bar stays pinned.
  main: (theme) => ({
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    paddingInline: { xs: theme.spacing(2), md: theme.spacing(4) },
    paddingBlock: { xs: theme.spacing(3), md: theme.spacing(3) },
  }),
  card: (theme) => ({
    width: '100%',
    maxWidth: 1000,
    backgroundColor: theme.palette.grey[100],
    borderRadius: '16px',
    overflow: 'hidden',
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
  }),
  formCol: (theme) => ({
    padding: { xs: theme.spacing(3), md: theme.spacing(4) },
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  }),
  heading: (theme) => ({
    ...theme.typography.displayItalic,
    fontSize: { xs: '1.75rem', md: '2rem' },
    lineHeight: 1.1,
    color: theme.palette.text.primary,
    margin: 0,
  }),
  intro: (theme) => ({
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.9375rem',
    lineHeight: 1.5,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
  }),
  nameRow: (theme) => ({
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(2),
  }),
  field: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: (theme) => ({
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: '0.8125rem',
    color: theme.palette.text.primary,
  }),
  req: (theme) => ({ color: theme.palette.secondary.main }),
  input: (theme) => ({
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.common.white,
      borderRadius: '8px',
    },
    '& .MuiInputBase-inputSizeSmall': {
      padding: '13px 14px',
      fontSize: '0.9375rem',
    },
  }),
  errorText: (theme) => ({
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.75rem',
    color: theme.palette.error.main,
  }),
  phonePrefix: (theme) => ({
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.9375rem',
    color: theme.palette.text.secondary,
    paddingRight: theme.spacing(1),
    marginRight: theme.spacing(1),
    borderRight: `1px solid ${theme.palette.divider}`,
    whiteSpace: 'nowrap',
  }),
  agreeRow: { display: 'flex', flexDirection: 'column', gap: '2px' },
  agreeLabel: (theme) => ({
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
  }),
  // Privacy hyperlink: black text, underlined to read as a link (not blue).
  privacyLink: (theme) => ({
    color: theme.palette.text.primary,
    textDecoration: 'underline',
    fontWeight: theme.typography.fontWeightRegular,
    '&:hover': { color: theme.palette.text.primary, textDecoration: 'underline' },
  }),
  submit: (theme) => ({
    marginTop: theme.spacing(1),
    height: 52,
    fontSize: '1rem',
  }),
  media: {
    display: { xs: 'none', md: 'block' },
    position: 'relative',
    minHeight: 480,
  },
  mediaImg: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  // Success state — a popup over the form rather than replacing it.
  sentDialog: (theme) => ({
    padding: { xs: theme.spacing(4), md: theme.spacing(5) },
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    alignItems: 'center',
    textAlign: 'center',
    width: { xs: '100%', sm: 420 },
  }),
  sentIcon: (theme) => ({
    width: 40,
    height: 40,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.action.selected,
  }),
  sentHeading: (theme) => ({
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeightBold,
    fontSize: '1.25rem',
    color: theme.palette.text.primary,
    margin: 0,
  }),
  sentBody: (theme) => ({
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.9375rem',
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
  }),
  sentButton: (theme) => ({ marginTop: theme.spacing(1) }),
};
